import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { Bundle } from '@/services/api/contracts/catalog/bundles';
import { useBundles, useCreatePurchaseOrderBundle } from '@/services/api/hooks/bundles';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { FC, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { SavePurchaseOrderFormFields } from '../SavePurchaseOrder';
import BundleSelectionCard from './BundleSelectionCard';
import { useBuildPOfromBundlePayload } from './useBuildPOfromBundlePayload';

export interface BundleWithQuantity {
  bundle: Bundle;
  quantity: number;
}

export const CreateFromBundlesModal: FC<ModalProps> = props => {
  const CreateFromBundles = () => {
    const { partnerId, companyId } = useParams();
    if (!(partnerId && companyId)) throw new Error('Missing params');

    const createPurchaseOrder = useCreatePurchaseOrderBundle();
    const buildPayload = useBuildPOfromBundlePayload(partnerId, companyId);

    const navigate = useNavigate();
    const routePrefix = useCustomerRoutePrefix();

    const methods = useForm<SavePurchaseOrderFormFields>({
      resolver: zodResolver(SavePurchaseOrderFormFields),
      defaultValues: {
        name: format(new Date(), 'dd/MM/yyyy HH:mm:SS')
      }
    });

    const [bundles] = useBundles(partnerId, companyId);
    const [bundlesWithQuantity, setBundlesWithQuantity] = useState<BundleWithQuantity[]>(
      bundles.map(bundle => {
        return {
          bundle,
          quantity: 0
        };
      })
    );

    const onBundleSelectedCallback = (bundleId: string, count: number) => {
      const bundle = bundlesWithQuantity.find(bundle => bundle.bundle.id === bundleId);
      if (!bundle) throw new Error('Failed product lookup');

      bundle.quantity = count;

      setBundlesWithQuantity([...bundlesWithQuantity]);
    };

    const onSubmit = async (data: SavePurchaseOrderFormFields) => {
      const bundlesToUse = bundlesWithQuantity.filter(bundle => bundle.quantity);

      const payload = buildPayload(data.name, bundlesToUse);

      const response = await createPurchaseOrder.mutateAsync(payload);

      navigate(`${routePrefix}/purchaseOrders/${response.id}/poCatalogue/deliveryDetails`);

      props.close();
    };

    const errorObj = createPurchaseOrder.error as Error | undefined;
    const error = errorObj ? errorObj.message : '';

    return (
      <Form className="flex w-full flex-col space-y-3 py-3" methods={methods} onSubmit={onSubmit}>
        <Card className="w-full p-8 space-y-3">
          <Modal.Controls {...props}>
            <h1 className="text-2xl font-semibold">Create PO from Bundles</h1>
          </Modal.Controls>
          <p>
            Give your order a name, and you'll be able to come back to it at any time from the orders / quotes screen.
          </p>

          <TextField id="name" label="Name" />

          <div className="overflow-y-auto max-h-[36rem] space-y-3">
            {bundlesWithQuantity.map(bundle => (
              <BundleSelectionCard
                key={bundle.bundle.id}
                bundleWithQuantity={bundle}
                onSelectCallback={onBundleSelectedCallback}
              />
            ))}
          </div>
          {error && <p className="text-red-500 mt-2">{error}</p>}
          <div className="flex justify-between pt-2 space-x-2">
            <Button onClick={props.close} className="w-fit" variant={ButtonVariant.Mono} type="button">
              Cancel
            </Button>
            <Button
              loading={createPurchaseOrder.isLoading}
              className="w-fit"
              variant={ButtonVariant.Primary}
              type="submit"
              disabled={bundlesWithQuantity.filter(bundle => bundle.quantity).length === 0}
            >
              Confirm
            </Button>
          </div>
        </Card>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-5xl" {...props}>
      <Suspense fallback={<Loading />}>
        <CreateFromBundles />
      </Suspense>
    </Modal>
  );
};
